import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import { MetricTypeCodes, SensorRoleTypeCodes } from '@kcftech/prometheus-client';
import { dataPlatformClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import {
    ensureIndicatorFilters,
    ensureIndicatorFiltersByIds,
} from 'custom_hooks/data_hooks/indicator/ensureIndicatorFilters';
import {
    excludeNullAndUndefined,
    isDefinedAndNotEmpty,
    isUndefinedOrEmpty,
} from 'utils/array';
import { buildDataPlatformOrderBy, toDataPlatformDataSourceResult } from 'utils/query_v3';

export type DataPlatformIndicatorFilter = Readonly<{
    groupIds?: string[];
    customFilterIds?: string[];
    indicatorTypes?: MetricTypeCodes[];
    sensorRoleTypes?: (SensorRoleTypeCodes | null)[];
    filtered?: boolean;
    orientations?: (DataPlatformApi.Orientation | null)[];
    dataTypes?: string[];
    calculations?: string[];
    bands?: (DataPlatformApi.IndicatorBandCode | null)[];
    labels?: string[];
    nameSearch?: string;
}>;

export type DataPlatformIndicatorDataSourceOptions = BaseDataSourceOptions<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

const getIndicatorFilterNameForIndicatorType = (
    indicatorType: MetricTypeCodes,
): string | undefined => {
    switch (indicatorType) {
        case MetricTypeCodes.VibrationOverallPeak:
            return 'Peak Velocity';
        case MetricTypeCodes.VibrationOverallPeakAccel:
            return 'Peak Acceleration';
        case MetricTypeCodes.VibrationOverallRms:
            return 'RMS Velocity';
        case MetricTypeCodes.VibrationOverallRmsAccel:
            return 'RMS Acceleration';
        case MetricTypeCodes.VibrationOverall:
            return 'Overall Vibration';
        case MetricTypeCodes.Voltage:
            return 'Voltage';
        case MetricTypeCodes.Temperature:
            return 'Temperature';
        case MetricTypeCodes.SignalStrength:
            return 'Signal Strength';
        case MetricTypeCodes.VibrationBandMax:
        case MetricTypeCodes.VibrationBandRms:
        case MetricTypeCodes.VibrationBandMaxAccel:
        case MetricTypeCodes.VibrationBandRmsAccel:
            return 'Vibration Bands';
        default:
            return undefined;
    }
};

export const mapSensorRoleTypeToAxis = (
    sensorRoleType: SensorRoleTypeCodes | null,
): DataPlatformApi.Axis | null => {
    switch (sensorRoleType) {
        case SensorRoleTypeCodes.VibrationXAxis:
            return DataPlatformApi.Axis.X;
        case SensorRoleTypeCodes.VibrationYAxis:
            return DataPlatformApi.Axis.Y;
        case SensorRoleTypeCodes.VibrationZAxis:
            return DataPlatformApi.Axis.Z;
        case null:
            return null;
        default:
            throw new Error('Attempted to filter for an unsupported sensor role type');
    }
};

const mapFiltersOntoIndicatorSpec = (
    axes: (DataPlatformApi.Axis | null)[] | undefined,
    orientations: (DataPlatformApi.Orientation | null)[] | undefined,
    filtered: boolean | undefined,
    indicatorSpec: DataPlatformApi.IndicatorSpec,
    label?: string,
): DataPlatformApi.IndicatorSpec[] => {
    // If no axes are provided, create a spec for each orientation
    if (axes === undefined || axes.length === 0) {
        // If no orientations are provided, create a single spec without axis or orientation
        if (orientations === undefined || orientations.length === 0) {
            return [{ ...indicatorSpec, filtered, label }];
        }

        return orientations.map((orientation) => ({
            ...indicatorSpec,
            orientation,
            filtered,
            label,
        }));
    }

    // If no orientations are provided, create a spec for each axis
    if (orientations === undefined || orientations.length === 0) {
        return axes.map((axis) => ({ ...indicatorSpec, axis, filtered, label }));
    }

    // Else, map a spec for each axis and orientation combination
    const indicatorSpecs: DataPlatformApi.IndicatorSpec[] = [];
    for (const axis of axes) {
        for (const orientation of orientations) {
            indicatorSpecs.push({ ...indicatorSpec, axis, orientation, filtered, label });
        }
    }

    return indicatorSpecs;
};

function collapse<T>(arr: T[] | undefined): (T | undefined)[] {
    return isUndefinedOrEmpty(arr) ? [undefined] : (arr as T[]);
}

function generateIndicatorSpecsForAllFilterProps(
    filter: DataPlatformIndicatorFilter,
    axes: (DataPlatformApi.Axis | null)[] | undefined,
): DataPlatformApi.IndicatorSpec[] {
    const specs: DataPlatformApi.IndicatorSpec[] = [];
    // axes and orientations are included on everything if they exist
    //
    // on the other hand, these:
    // // dataTypes, calculations, band, labels
    // combine with each other via OR
    for (const dataType of collapse(filter.dataTypes)) {
        for (const calculation of collapse(filter.calculations)) {
            for (const band of collapse(filter.bands)) {
                for (const label of collapse(filter.labels)) {
                    specs.push(
                        ...mapFiltersOntoIndicatorSpec(
                            axes,
                            filter.orientations,
                            filter.filtered,
                            {
                                physicalQuantity: dataType,
                                statistic: calculation,
                                band,
                                label,
                            },
                        ),
                    );
                }
            }
        }
    }

    return specs;
}

const getIndicatorSpecsForOtherFilters = (
    filter: DataPlatformIndicatorFilter,
    axes: (DataPlatformApi.Axis | null)[] | undefined,
): DataPlatformApi.IndicatorSpec[] => {
    const indicatorSpecs: DataPlatformApi.IndicatorSpec[] = [];

    const hasAnyOtherFilters =
        isDefinedAndNotEmpty(filter.dataTypes) ||
        isDefinedAndNotEmpty(filter.calculations) ||
        isDefinedAndNotEmpty(filter.bands) ||
        isDefinedAndNotEmpty(filter.labels);

    if (hasAnyOtherFilters) {
        // Combination of any/all 4 filters: dataTypes, calculations, bands, labels
        indicatorSpecs.push(...generateIndicatorSpecsForAllFilterProps(filter, axes));
    }

    // If no other filters are provided, create a spec with only the axes and orientations
    if (
        !hasAnyOtherFilters &&
        (isDefinedAndNotEmpty(axes) ||
            isDefinedAndNotEmpty(filter.orientations) ||
            filter.filtered !== undefined ||
            filter.nameSearch)
    ) {
        indicatorSpecs.push(
            ...mapFiltersOntoIndicatorSpec(
                axes,
                filter.orientations,
                filter.filtered,
                {},
            ),
        );
    }

    return indicatorSpecs;
};

export const getIndicatorSpecs = (
    filter: DataPlatformIndicatorFilter,
    defaultFilters: DataPlatformApi.FilterResponse[],
) => {
    const axes = filter.sensorRoleTypes
        ?.filter(
            (srt) =>
                srt === SensorRoleTypeCodes.VibrationXAxis ||
                srt === SensorRoleTypeCodes.VibrationYAxis ||
                srt === SensorRoleTypeCodes.VibrationZAxis ||
                srt === null,
        )
        .map(mapSensorRoleTypeToAxis);

    const specs = getIndicatorSpecsForOtherFilters(filter, axes);

    if (filter.indicatorTypes) {
        const uniqueIndicatorFilterNames = new Set(
            excludeNullAndUndefined(
                filter.indicatorTypes.map(getIndicatorFilterNameForIndicatorType),
            ),
        );

        const indicatorSpecs = defaultFilters
            .filter((filter) => uniqueIndicatorFilterNames.has(filter.name))
            .flatMap((filter) => filter.filter);

        const indicatorTypeSpecs = indicatorSpecs.flatMap((spec) =>
            mapFiltersOntoIndicatorSpec(axes, filter.orientations, filter.filtered, spec),
        );

        specs.push(...indicatorTypeSpecs);
    }

    return specs;
};

const queryFn: DataSourceQuery<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
> = async ({ filter, sort, page, pageLimit }) => {
    const orderBy = buildDataPlatformOrderBy(sort);

    const defaultFilters = await ensureIndicatorFilters();
    const customFilters = await ensureIndicatorFiltersByIds(filter.customFilterIds ?? []);

    return toDataPlatformDataSourceResult(
        dataPlatformClient.indicators.getIndicators({
            groupIds: filter.groupIds ?? [],
            indicatorSpecs: [
                ...getIndicatorSpecs(filter, defaultFilters),
                ...customFilters.flatMap((filter) => filter.filter),
            ],
            limit: pageLimit,
            offset: (page - 1) * pageLimit,
            sort: orderBy,
        }),
    );
};

export type IndicatorPagedDataPlatformDataSource = PagedDataSource<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

export function usePagedDataPlatformIndicatorDataSource(
    options: DataPlatformIndicatorDataSourceOptions,
): IndicatorPagedDataPlatformDataSource {
    return usePagedDataSource({
        ...options,
        query: queryFn,
    });
}

export type IndicatorInfiniteDataPlatformDataSource = InfiniteDataSource<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

export function useInfiniteDataPlatformIndicatorDataSource(
    options: DataPlatformIndicatorDataSourceOptions,
): IndicatorInfiniteDataPlatformDataSource {
    return useInfiniteDataSource({
        defaultSort: undefined,
        ...options,
        query: queryFn,
    });
}
